import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const rows = [
  { k: "ROLE", v: "SOLO AI BUILDER" },
  { k: "STACK", v: "NEXT.JS / REACT / PYTHON / AI" },
  { k: "FOCUS", v: "AUTONOMOUS SYSTEMS" },
  { k: "AVAILABILITY", v: "OPEN FOR WORK" },
  { k: "EXPERIENCE", v: "5+ YEARS SHIPPING" },
  { k: "STATUS", v: "SHIPPING NOW" },
];

export default function SceneIndex() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "+=260%",
          scrub: 1,
          pin: true,
        },
      });

      tl.from(".index-r", { xPercent: -30, opacity: 0, duration: 0.6 }, 0);

      rows.forEach((_, i) => {
        tl.fromTo(
          `.spec-row-${i}`,
          { xPercent: 30, opacity: 0 },
          { xPercent: 0, opacity: 1, duration: 0.4 },
          0.4 + i * 0.25,
        );
        tl.to(`.spec-row-${i} .spec-value`, {
          color: "#FF5A1F",
          duration: 0.2,
        }, 0.55 + i * 0.25);
      });

      // arm indicator sweeps down
      tl.to(".arm-indicator", {
        top: "85%",
        duration: rows.length * 0.25,
        ease: "none",
      }, 0.5);

      // compress rows into single line
      tl.to(".spec-list", {
        scaleY: 0.05,
        transformOrigin: "50% 50%",
        duration: 0.6,
      }, 2.4);

      tl.to(".compressed-line", {
        opacity: 1,
        duration: 0.4,
      }, 2.6);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="scene-index"
      ref={root}
      data-theme="dark"
      className="relative h-screen w-full overflow-hidden ink-bg grain"
    >
      <div className="absolute top-24 left-6 md:left-10 type-label text-[color:var(--gray)]">
        05 / BUILDER INDEX
      </div>
      <div className="absolute top-24 right-6 md:right-10 type-label text-[color:var(--gray)] text-right">
        SPEC · DEFINITIVE
      </div>

      {/* huge stationary R */}
      <div className="index-r absolute left-0 top-0 h-full flex items-center pointer-events-none">
        <div className="type-display text-[color:var(--paper)] leading-none"
             style={{ fontSize: "90vh" }}>
          R
        </div>
      </div>

      {/* R's horizontal arm marker */}
      <div className="arm-indicator absolute left-0 right-0 h-[2px] bg-[color:var(--signal)]/60 pointer-events-none"
           style={{ top: "35%" }} />

      {/* Rows */}
      <div className="spec-list absolute right-6 md:right-16 top-1/2 -translate-y-1/2 w-[55vw] max-w-[720px] flex flex-col gap-2">
        {rows.map((r, i) => (
          <div
            key={r.k}
            className={`spec-row-${i} flex items-baseline justify-between border-b border-[color:var(--dark)] pb-2`}
          >
            <div className="type-mono text-[11px] text-[color:var(--gray)]">
              {String(i + 1).padStart(2, "0")} · {r.k}
            </div>
            <div className="spec-value type-display text-[color:var(--paper)] text-2xl md:text-3xl tabular">
              {r.v}
            </div>
          </div>
        ))}
      </div>

      <div className="compressed-line absolute bottom-24 left-6 md:left-16 right-6 opacity-0 type-mono text-xs text-[color:var(--signal)] tabular">
        ROLE · SOLO AI BUILDER — STACK · NEXT.JS / REACT / PYTHON / AI — FOCUS · AUTONOMOUS SYSTEMS — AVAILABILITY · OPEN — EXPERIENCE · 5+YRS — STATUS · SHIPPING NOW
      </div>

      <div className="absolute bottom-6 left-0 right-0 px-6 md:px-10 flex items-end justify-between">
        <div className="type-label text-[color:var(--gray)]">DEFINITIVE INDEX / 05</div>
        <div className="type-label text-[color:var(--gray)]">ROWS: 06</div>
      </div>
    </section>
  );
}